import { useAccountDrawerController } from 'context/accountDrawerController';
import { parseTaskType, DrawerTab } from 'context/accountDrawer/types';
import useSearchNextTask from 'hooks/services/task/useSearchNextTask';
import useGetTask from 'hooks/services/task/useGetTask';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import { useHideTaskConstruction } from '../hideTaskConstructionContext';
import { NextTaskSearch } from 'crono-fe-common/types/DTO/nextTaskSearch';
import Gleap from 'gleap';

interface TaskNavState {
  ids: number[];
  pointer: number;
}

const emptyTaskNav: TaskNavState = { ids: [], pointer: -1 };

// Reconciles the visited-task history when a new task loads, so Previous/Next can
// navigate the same-day flow purely on the FE (the BE only tells us the nextId).
const reconcileTaskNav = (prev: TaskNavState, newId: number): TaskNavState => {
  if (prev.ids.length === 0) {
    return { ids: [newId], pointer: 0 };
  }
  if (prev.ids[prev.pointer] === newId) {
    return prev;
  }
  if (prev.pointer > 0 && prev.ids[prev.pointer - 1] === newId) {
    return { ids: prev.ids, pointer: prev.pointer - 1 };
  }
  if (
    prev.pointer < prev.ids.length - 1 &&
    prev.ids[prev.pointer + 1] === newId
  ) {
    return { ids: prev.ids, pointer: prev.pointer + 1 };
  }
  return {
    ids: [...prev.ids.slice(0, prev.pointer + 1), newId],
    pointer: prev.pointer + 1,
  };
};

// Drops the current task from the history (used when it is skipped/completed, so it
// stops being a Previous/Next target) and steps the pointer back to the one before it.
// Advancing to the following task then reconciles the pointer forward again.
const removeCurrentFromNav = (prev: TaskNavState): TaskNavState => {
  if (prev.pointer < 0) {
    return prev;
  }
  return {
    ids: [
      ...prev.ids.slice(0, prev.pointer),
      ...prev.ids.slice(prev.pointer + 1),
    ],
    pointer: prev.pointer - 1,
  };
};

// Every filter search/next takes, required so a filter can't be left out when
// the board builds them (see getTaskFilterFields).
export type NextTaskFilters = Required<
  Pick<
    NextTaskSearch,
    | 'types'
    | 'fromCrm'
    | 'sortBy'
    | 'hasAutomationError'
    | 'strategyIds'
    | 'accountIds'
    | 'accountListIds'
    | 'prospectListIds'
    | 'leadListIds'
    | 'accountExternalProperties'
    | 'prospectExternalProperties'
    | 'leadExternalProperties'
    | 'hasOpportunity'
    | 'leadScoreLevels'
    | 'prospectScoreLevels'
    | 'accountScoreLevels'
    | 'timeZones'
    | 'noTimeZone'
    | 'countryCodes'
    | 'noCountryCode'
  >
>;

// How the board started the flow. `sequential` is "open N tasks": the flow
// walks itself forward as tasks are completed. `standalone` is a single task
// clicked on the board: it can still be navigated, but completing a task ends
// it. The mode is chosen once at start and never re-derived, so navigation and
// filter changes cannot silently turn one flow into the other.
export type FlowMode = 'sequential' | 'standalone';

// How the user left the task. `abandoned` means it stopped existing while it
// was open (its contact was re-assigned, which stops the strategy and its
// tasks), so the flow may only move forward past it.
export type TaskHandledReason = 'completed' | 'skipped' | 'abandoned';

type StartMultipleExecutionParams = {
  mode: FlowMode;
  startTaskId: number | null;
  filters: NextTaskFilters;
  // Ordered task ids from the first pending task up to and including the one
  // being started, so Previous can step back when seeding the flow from a
  // task in the middle of the board (the BE only ever returns a forward
  // `nextId`). Omitted for a first-task start, which has nothing behind it.
  initialNavIds?: number[];
};

interface ExecuteMultipleTasksContextType {
  currentTaskBeingExecuted: TaskTodo | null;
  // Whether a multi-tasks flow is running: what tells an action that repoints
  // the drawer (e.g. re-assigning a contact) that it would cancel a flow.
  isFlowActive: boolean;
  canGoPrevious: boolean;
  canGoNext: boolean;
  cancelMultipleExecution: () => void;
  goToPreviousTask: () => void;
  goToNextTask: () => void;
  onTaskHandled: (
    reason: TaskHandledReason,
    forwardNextId?: number | null,
  ) => void;
  startMultipleExecution: (params: StartMultipleExecutionParams) => void;
  updateFlowFilters: (filters: NextTaskFilters) => void;
  executeSingleTask: (
    task: TaskTodo,
    options?: { keepDrawerOpen?: boolean; returnTab?: DrawerTab },
  ) => void;
}

export const ExecuteMultipleTasksContext = createContext<
  ExecuteMultipleTasksContextType | undefined
>(undefined);

ExecuteMultipleTasksContext.displayName = 'ExecuteMultipleTasksContext';

const ExecuteMultipleTasksProvider: FC<PropsWithChildren> = ({ children }) => {
  const { openTaskExecutionDrawer, closeDrawer, removeModal, drawerContent } =
    useAccountDrawerController();

  const [startTaskId, setStartTaskId] = useState<number | null | undefined>(
    undefined,
  );

  // The filters that scope the multi-tasks flow. Set when the flow starts and
  // cleared when it ends: `undefined` means "no flow active" (the next-task
  // query stays disabled), while a value means the flow is running.
  const [filters, setFilters] = useState<NextTaskFilters | undefined>(
    undefined,
  );

  const [taskBeingExecuted, setTaskBeingExecuted] = useState<TaskTodo | null>(
    null,
  );

  // FE-only navigation state: history of visited task ids + the frontier nextId
  const [taskNav, setTaskNav] = useState<TaskNavState>(emptyTaskNav);
  const [latestNextId, setLatestNextId] = useState<number | null>(null);

  // Bumped on every (re)start so the next-task query cannot be served a
  // response an earlier run already consumed — see useSearchNextTask.
  const [flowRunId, setFlowRunId] = useState(0);

  // Mode of the running flow, `null` when none is running. A ref rather than
  // state because the drawer's completion callback is captured when a task is
  // opened: reading the mode from that closure would return the value it had
  // when the task opened, not the one the flow has when it is completed.
  const flowModeRef = useRef<FlowMode | null>(null);

  // Live mirror of taskNav for advancePastCurrentTask: completion invokes it
  // from a closure captured when the task was opened, where the taskNav state
  // value is stale. Synced in an effect (see below) so the mirror only ever
  // reflects committed renders; the ref is only read from async callbacks.
  const taskNavRef = useRef(taskNav);

  // Handle of the delayed drawer opening scheduled by performTask, so ending
  // the flow (e.g. the user closing the drawer during that delay) can drop the
  // pending open instead of letting it re-open the drawer afterwards.
  const openDrawerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isFlowActive = startTaskId !== undefined && filters !== undefined;

  const {
    data: taskToExecute,
    isLoading: isLoadingTask,
    isRefetching: isRefetchingTask,
  } = useSearchNextTask(
    {
      id: startTaskId ?? null,
      ...filters,
      sortBy: filters?.sortBy ?? 'CreatedDate',
    },
    isFlowActive,
    flowRunId,
  );

  const { setHideProgressTask } = useHideTaskConstruction();
  const { mutateAsync: getTaskAsync } = useGetTask();

  const canGoPrevious = taskNav.pointer > 0;
  const canGoNext =
    taskNav.ids.length > 0 &&
    (taskNav.pointer < taskNav.ids.length - 1 || latestNextId !== null);

  const closeDrawerWithDelay = useCallback(() => {
    setTimeout(closeDrawer, 200);
  }, [closeDrawer]);

  const cancelMultipleExecution = useCallback(() => {
    if (openDrawerTimeoutRef.current !== null) {
      clearTimeout(openDrawerTimeoutRef.current);
      openDrawerTimeoutRef.current = null;
    }
    setStartTaskId(undefined);
    setFilters(undefined);
    setTaskBeingExecuted(null);
    setTaskNav(emptyTaskNav);
    setLatestNextId(null);
    flowModeRef.current = null;
  }, []);

  // The two ways a flow can end. A standalone flow keeps the drawer open on the
  // account tab (the task was opened from there and the user is not walking a
  // list), a sequential one closes it once there is nothing left to execute.
  const endFlow = useCallback(
    ({ keepDrawer }: { keepDrawer: boolean }) => {
      cancelMultipleExecution();
      if (keepDrawer) {
        setHideProgressTask(false);
        removeModal('account');
      } else {
        closeDrawerWithDelay();
      }
    },
    [
      cancelMultipleExecution,
      closeDrawerWithDelay,
      removeModal,
      setHideProgressTask,
    ],
  );

  const goToPreviousTask = useCallback(() => {
    if (!canGoPrevious) {
      return;
    }
    setStartTaskId(taskNav.ids[taskNav.pointer - 1]);
  }, [canGoPrevious, taskNav]);

  const goToNextTask = useCallback(() => {
    if (!canGoNext) {
      return;
    }
    // Prefer the current task's live successor (the BE frontier `nextId`
    // refreshed on every load) over the frozen visited history: a task created
    // mid-flow lands in the frontier but not in the history, so stepping
    // through history would skip it. This matches `advancePastCurrentTask`
    // (completing/skipping already picks the new task up). Fall back to
    // history only when the current task has no known successor.
    if (latestNextId !== null) {
      setStartTaskId(latestNextId);
      return;
    }
    setStartTaskId(taskNav.ids[taskNav.pointer + 1]);
  }, [canGoNext, taskNav, latestNextId]);

  // The single advance path once the current task has been handled (skipped or
  // completed): drop it from the history so it can't be navigated back to,
  // then move to the next task. Completion passes the nextId its response
  // carried for the handled task; skip passes nothing and uses the latest
  // loaded one. When no forward task is left, fall back to the previous
  // still-pending task; only when nothing remains end the flow.
  const advancePastCurrentTask = useCallback(
    (
      forwardNextId?: number | null,
      { allowPrevious = true }: { allowPrevious?: boolean } = {},
    ) => {
      const nextId = forwardNextId !== undefined ? forwardNextId : latestNextId;
      const nav = taskNavRef.current;
      const previousId =
        allowPrevious && nav.pointer > 0 ? nav.ids[nav.pointer - 1] : null;
      const target = nextId ?? previousId;
      if (target === null) {
        endFlow({ keepDrawer: flowModeRef.current === 'standalone' });
        return;
      }
      setStartTaskId(target);
      setTaskNav((prev) => removeCurrentFromNav(prev));
    },
    [latestNextId, endFlow],
  );

  // Single exit point for a task the user is done with, so the ways out can
  // never disagree: skipping always walks the flow, while completing or
  // abandoning walks it only in a sequential flow — a standalone one ends
  // there instead, since the user opened that one task and nothing else.
  // An abandoned task also never falls back to the previous task: it no longer
  // exists, so the flow either moves on to the next one or is over.
  const onTaskHandled = useCallback(
    (reason: TaskHandledReason, forwardNextId?: number | null) => {
      const endsStandaloneFlow =
        reason === 'completed' || reason === 'abandoned';
      if (endsStandaloneFlow && flowModeRef.current === 'standalone') {
        endFlow({ keepDrawer: true });
        return;
      }
      advancePastCurrentTask(forwardNextId, {
        allowPrevious: reason !== 'abandoned',
      });
    },
    [advancePastCurrentTask, endFlow],
  );

  const performTask = useCallback(
    async (
      task: TaskTodo,
      executeOnSuccess?: () => void,
      isSingleExecution?: boolean,
      nextId?: number | null,
    ) => {
      //To avoid multiple calls on the same task, may happen due useEffect being called when refocus on the crono window
      if (taskBeingExecuted?.id === task.id) return;

      //Mark the task as executing before awaiting the template fetch, so a re-entrant
      //call during the await hits the duplicate guard above instead of firing again
      setTaskBeingExecuted(task);

      //The Timeline/Search list payloads omit the template for performance, so a task opened from
      //there arrives without it. Resolve it on demand (BE includes the personalized overlay) before
      //executing, so the template is preselected just like when opening from the task board.
      let resolvedTask = task;
      if (task.templateId != null && !task.template) {
        try {
          const res = await getTaskAsync(
            withMeta(
              { id: task.id, withTemplate: true },
              { silentError: true },
            ),
          );
          if (res?.data?.data?.template) {
            resolvedTask = { ...task, template: res.data.data.template };
            setTaskBeingExecuted(resolvedTask);
          }
        } catch {
          //If the fetch fails we still open the task, just without the preselected template
        }
      }

      // Record this task in the FE navigation history and remember the frontier
      // nextId the BE returned for it, so Previous/Next can step through the
      // flow. Skipped for a single task execution, which has no surrounding
      // flow to navigate.
      if (!isSingleExecution) {
        setTaskNav((prev) => reconcileTaskNav(prev, task.id));
        setLatestNextId(nextId ?? null);
      }

      setHideProgressTask(true);
      const tabValue = parseTaskType(resolvedTask.type);
      openDrawerTimeoutRef.current = setTimeout(() => {
        openDrawerTimeoutRef.current = null;
        openTaskExecutionDrawer({
          accountId: resolvedTask.account.objectId,
          prospectId: resolvedTask.prospect?.objectId ?? null,
          task: resolvedTask,
          tab: tabValue,
          defaultOpenTemplateTab: false,
          callOnSuccess: () => {
            //To hide all the changes to the sideTab that are performed during the change of the task
            setHideProgressTask(true);

            Gleap.trackEvent('task_execution');

            //Clear to correctly perform the flow
            setTaskBeingExecuted(null);
            if (executeOnSuccess) executeOnSuccess();
            if (
              !isSingleExecution &&
              startTaskId !== undefined &&
              filters !== undefined
            ) {
              // The id of the task after this one is captured as nextId when the
              // task was opened (per-task fresh, since performTask re-runs on
              // every visit), so it is the correct forward target for the task
              // just completed.
              onTaskHandled('completed', nextId ?? null);
            }
          },
          forceReload: true,
        });
      }, 500);
    },
    [
      startTaskId,
      filters,
      taskBeingExecuted,
      onTaskHandled,
      getTaskAsync,
      openTaskExecutionDrawer,
      setHideProgressTask,
    ],
  );

  // Starts a multi-tasks flow. `mode` says which of the board's two entry
  // points opened it and holds for the whole flow; `startTaskId` is the task to
  // start from (null starts from the first pending one) and the other fields
  // scope it.
  const startMultipleExecution = useCallback(
    ({
      mode,
      startTaskId,
      filters,
      initialNavIds,
    }: StartMultipleExecutionParams) => {
      flowModeRef.current = mode;
      setFlowRunId((id) => id + 1);
      setStartTaskId(startTaskId);
      setFilters(filters);
      setTaskNav(
        initialNavIds && initialNavIds.length
          ? { ids: initialNavIds, pointer: initialNavIds.length - 1 }
          : emptyTaskNav,
      );
      setLatestNextId(null);
    },
    [],
  );

  // The board's filters changed while a flow is running: rescope it and restart
  // from the first task matching the new filters, keeping the mode it was
  // started with. A no-op when no flow is running.
  const updateFlowFilters = useCallback((newFilters: NextTaskFilters) => {
    if (flowModeRef.current === null) {
      return;
    }
    setFlowRunId((id) => id + 1);
    setStartTaskId(null);
    setFilters(newFilters);
    setTaskNav(emptyTaskNav);
    setLatestNextId(null);
  }, []);

  const executeSingleTask = useCallback(
    (
      task: TaskTodo,
      options?: { keepDrawerOpen?: boolean; returnTab?: DrawerTab },
    ) => {
      // A single task is not a group execution: end any active multi-tasks flow
      // first so its navigation history and next-task query don't leak into
      // (or resurrect over) this one. The flag is still needed because
      // callOnSuccess captured the pre-cancel flow state.
      cancelMultipleExecution();
      const onSuccess = () => {
        cancelMultipleExecution();
        if (options?.keepDrawerOpen) {
          setHideProgressTask(false);
          removeModal(options.returnTab);
        } else {
          closeDrawerWithDelay();
        }
      };
      performTask(task, onSuccess, true);
    },
    [
      cancelMultipleExecution,
      performTask,
      closeDrawerWithDelay,
      setHideProgressTask,
      removeModal,
    ],
  );

  // Keep the taskNav mirror in sync after each committed render (see taskNavRef).
  useEffect(() => {
    taskNavRef.current = taskNav;
  }, [taskNav]);

  // This is done to clear the tasks when the modal is closed/changed with a
  // manual action of the same type or different. Deps are intentionally
  // limited to the drawer identity: `taskBeingExecuted` must NOT be a
  // dependency, otherwise the flow would cancel itself mid-advance;
  // `performTask` sets the new task ~500ms before it opens the drawer, so the
  // two briefly disagree during a normal advance.
  useEffect(() => {
    if (
      taskBeingExecuted &&
      (drawerContent?.modalVisible !== taskBeingExecuted?.type.toLowerCase() ||
        drawerContent?.task?.id !== taskBeingExecuted?.id)
    ) {
      cancelMultipleExecution();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [drawerContent?.modalVisible, drawerContent?.task?.id]);

  useEffect(() => {
    // Cancelling the flow only disables the next-task query, it does not drop
    // the response it already holds (the query key of an unfiltered flow is the
    // same one the disabled query falls back to). Without this guard, closing
    // the drawer would clear `taskBeingExecuted` and immediately re-open that
    // leftover task.
    if (!isFlowActive || !taskToExecute || isLoadingTask || isRefetchingTask)
      return;
    if (taskToExecute?.data?.data === null) {
      //No more task to execute
      endFlow({ keepDrawer: flowModeRef.current === 'standalone' });
    } else if (
      taskToExecute?.data?.data &&
      //To avoid multiple calls on the same task, may happen due useEffect being called when refocus on the crono window
      (!taskBeingExecuted?.id ||
        taskToExecute?.data?.data?.id !== taskBeingExecuted?.id)
    ) {
      performTask(
        taskToExecute.data.data,
        undefined,
        false,
        taskToExecute.data.nextId ?? null,
      );
    }
  }, [
    startTaskId,
    filters,
    isFlowActive,
    isLoadingTask,
    isRefetchingTask,
    taskBeingExecuted?.id,
    taskToExecute,
    endFlow,
    performTask,
  ]);

  const value = useMemo<ExecuteMultipleTasksContextType>(
    () => ({
      currentTaskBeingExecuted: taskBeingExecuted,
      isFlowActive,
      canGoPrevious,
      canGoNext,
      cancelMultipleExecution,
      goToPreviousTask,
      goToNextTask,
      onTaskHandled,
      startMultipleExecution,
      updateFlowFilters,
      executeSingleTask,
    }),
    [
      taskBeingExecuted,
      isFlowActive,
      canGoPrevious,
      canGoNext,
      cancelMultipleExecution,
      goToPreviousTask,
      goToNextTask,
      onTaskHandled,
      startMultipleExecution,
      updateFlowFilters,
      executeSingleTask,
    ],
  );

  return (
    <ExecuteMultipleTasksContext.Provider value={value}>
      {children}
    </ExecuteMultipleTasksContext.Provider>
  );
};

export default ExecuteMultipleTasksProvider;

export function useExecuteMultipleTasks() {
  const context = useContext(ExecuteMultipleTasksContext);
  if (context === undefined) {
    throw new Error(
      `useExecuteMultipleTasks must be used within a ExecuteMultipleTasksProvider`,
    );
  }

  return context;
}
